import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
} from '@heroicons/react/24/outline'
import { productService } from '../../services/productService'
import toast from 'react-hot-toast'

export const CreateProductPage = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id } = useParams<{ id: string }>()
  const isEditMode = Boolean(id)

  const [formData, setFormData] = useState({
    product_name: '',
    sku: '',
    barcode: '',
    description: '',
    brand_id: '',
    category_id: '',
    cost_price: '',
    retail_price: '',
    wholesale_price: '',
    special_price: '',
    track_serial_numbers: true,
    warranty_period_months: '12',
    reorder_level: '5',
  })

  const { data: brandsData } = useQuery({
    queryKey: ['brands'],
    queryFn: () => productService.getBrands(),
  })

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => productService.getCategories(),
  })

  const { data: productData, isLoading: isLoadingProduct } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productService.getById(id!),
    enabled: isEditMode,
  })

  useEffect(() => {
    if (isEditMode && productData?.data?.data) {
      const product = productData.data.data
      setFormData({
        product_name: product.product_name || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        description: product.description || '',
        brand_id: product.brand_id?.toString() || '',
        category_id: product.category_id?.toString() || '',
        cost_price: product.cost_price?.toString() || '',
        retail_price: product.retail_price?.toString() || '',
        wholesale_price: product.wholesale_price?.toString() || '',
        special_price: product.special_price?.toString() || '',
        track_serial_numbers: product.track_serial_numbers ?? true,
        warranty_period_months: product.warranty_months?.toString() || '12',
        reorder_level: product.reorder_level?.toString() || '5',
      })
    }
  }, [isEditMode, productData])

  const createMutation = useMutation({
    mutationFn: (data: any) => productService.create(data),
    onSuccess: () => {
      toast.success('Product created successfully')
      queryClient.invalidateQueries({ queryKey: ['products'] })
      navigate('/products')
    },
    onError: () => {
      toast.error('Failed to create product')
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: any) => productService.update(id!, data),
    onSuccess: () => {
      toast.success('Product updated successfully')
      queryClient.invalidateQueries({ queryKey: ['products'] })
      navigate(`/products/${id}`)
    },
    onError: () => {
      toast.error('Failed to update product')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const payload = {
      ...formData,
      brand_id: formData.brand_id ? Number(formData.brand_id) : null,
      category_id: formData.category_id ? Number(formData.category_id) : null,
      cost_price: Number(formData.cost_price),
      retail_price: Number(formData.retail_price),
      wholesale_price: Number(formData.wholesale_price) || null,
      special_price: Number(formData.special_price) || null,
      warranty_period_months: Number(formData.warranty_period_months),
      reorder_level: Number(formData.reorder_level),
    }
    
    if (isEditMode) {
      updateMutation.mutate(payload)
    } else {
      createMutation.mutate(payload)
    }
  }

  const brands = brandsData?.data?.data || []
  const categories = categoriesData?.data?.data || []

  if (isEditMode && isLoadingProduct) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(isEditMode ? `/products/${id}` : '/products')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Product' : 'Add New Product'}
          </h1>
          <p className="text-sm text-gray-500">
            {isEditMode ? 'Update product details' : 'Create a new product in your inventory'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                <input
                  type="text"
                  required
                  value={formData.product_name}
                  onChange={(e) => setFormData(p => ({ ...p, product_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., Canon EOS R6 Camera Body"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SKU *</label>
                <input
                  type="text"
                  required
                  value={formData.sku}
                  onChange={(e) => setFormData(p => ({ ...p, sku: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., CAN-R6-BODY"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
                <input
                  type="text"
                  value={formData.barcode}
                  onChange={(e) => setFormData(p => ({ ...p, barcode: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Scan or enter barcode"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Product description..."
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price (KSh) *</label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.cost_price}
                  onChange={(e) => setFormData(p => ({ ...p, cost_price: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Retail Price (KSh) *</label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.retail_price}
                  onChange={(e) => setFormData(p => ({ ...p, retail_price: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Wholesale Price (KSh)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.wholesale_price}
                  onChange={(e) => setFormData(p => ({ ...p, wholesale_price: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Special Price (KSh)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.special_price}
                  onChange={(e) => setFormData(p => ({ ...p, special_price: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Organization */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Info</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                <select
                  value={formData.brand_id}
                  onChange={(e) => setFormData(p => ({ ...p, brand_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select Brand</option>
                  {brands.map((brand: any) => (
                    <option key={brand.brand_id} value={brand.brand_id}>{brand.brand_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData(p => ({ ...p, category_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select Category</option>
                  {categories.map((cat: any) => (
                    <option key={cat.category_id} value={cat.category_id}>{cat.category_name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Inventory Settings */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Inventory Settings</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="track_serials"
                  checked={formData.track_serial_numbers}
                  onChange={(e) => setFormData(p => ({ ...p, track_serial_numbers: e.target.checked }))}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300"
                />
                <label htmlFor="track_serials" className="text-sm text-gray-700">
                  Track Serial Numbers
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Warranty (Months)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.warranty_period_months}
                  onChange={(e) => setFormData(p => ({ ...p, warranty_period_months: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Level</label>
                <input
                  type="number"
                  min="0"
                  value={formData.reorder_level}
                  onChange={(e) => setFormData(p => ({ ...p, reorder_level: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate(isEditMode ? `/products/${id}` : '/products')}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isEditMode 
                ? (updateMutation.isPending ? 'Updating...' : 'Update Product')
                : (createMutation.isPending ? 'Creating...' : 'Create Product')
              }
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
